import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter, Tooltip } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { useUnit } from 'effector-react';
import { FileSearch, HelpCircle } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountReport } from 'shared/api';
import { TIME_ZONES } from 'shared/config';
import { translated } from 'shared/lib';
import { accountRoute } from 'shared/routing';
import {
	createDateFilter,
	createRegistry,
	MultiSelect,
	RegistryRootProps,
	RegistryTableTfootProps,
} from 'shared/ui';
import { AccountReportRegistryModel, accountTreeModel } from '../../model';
import cls from './account-report-registry.module.scss';

export type AccountReportRegistryProps = RegistryRootProps & {
	emptyActionSlot?: ReactNode;
	topBarActionSlot?: ReactNode;
	tableTdActionSlot?: (accountReport: AccountReport) => ReactNode;
};

export const createAccountReportRegistry = ({
	$total,
	$isMultiCurrencies,
	model,
	dateFilterModel,
}: AccountReportRegistryModel) => {
	const Registry = createRegistry(model);

	const DateFilter = createDateFilter(dateFilterModel);

	const DateField = reflect({
		view: DateFilter.DateField,
		bind: {
			className: cls.accountReportRegistryDateFilter,
		},
	});

	const TimeZoneField = reflect({
		view: DateFilter.TimeZoneField,
		bind: {
			className: cls.accountReportRegistryTimeZoneFilter,
			value: model.filters.timeZone.$value.map((tz) => tz.toString()),
			onChange: (value) =>
				model.filters.timeZone.setValue(Number(value ?? TIME_ZONES['UTC-00:00'])),
		},
	});

	const CurrenciesField = reflect({
		view: MultiSelect,
		bind: {
			label: translated('account-report-registry.filters.currencies.label'),
			placeholder: translated('account-report-registry.filters.currencies.placeholder'),
			className: cls.accountReportRegistryCurrenciesFilter,
			value: model.filters.currencies.$value,
			onChange: (value) => model.filters.currencies.setValue(value),
			data: accountTreeModel.$currencies,
			searchable: true,
		},
	});

	const AccountLink = reflect({
		view: Anchor<typeof Link>,
		bind: {
			to: accountRoute,
			component: Link,
		},
	});

	const AccountReportRegistryTotal = forwardRef<HTMLTableSectionElement, RegistryTableTfootProps>(
		(props, ref) => {
			const [total, isMultiCurrencies] = useUnit([$total, $isMultiCurrencies]);

			const { t } = useTranslation();

			if (!total) return null;

			return (
				<Registry.Table.Tfoot
					ref={ref}
					{...props}
				>
					<Registry.Table.Tr>
						<Registry.Table.Td colSpan={1}>
							{t('account-report-registry.table-footer.total')}
						</Registry.Table.Td>
						<Registry.Table.Td>{!isMultiCurrencies && total.currency}</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.deposits} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.withdrawals} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.jackpots} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.bonuses} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.income} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							{!isMultiCurrencies && <NumberFormatter value={total.incomeAllSources} />}
						</Registry.Table.Td>
						<Registry.Table.Td>
							<NumberFormatter
								value={total.rtp}
								suffix=' %'
							/>
						</Registry.Table.Td>
						<Registry.Table.Td>
							<NumberFormatter
								value={total.rtpAllSources}
								suffix=' %'
							/>
						</Registry.Table.Td>
						<Registry.Table.Td>
							<NumberFormatter
								value={total.numberOfBets}
								decimalScale={0}
							/>
						</Registry.Table.Td>
					</Registry.Table.Tr>
				</Registry.Table.Tfoot>
			);
		},
	);

	const AccountReportRegistry = forwardRef<HTMLDivElement, AccountReportRegistryProps>(
		({ emptyActionSlot, topBarActionSlot, tableTdActionSlot, ...props }, ref) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					<Registry.Title>{t('account-report-registry.title')}</Registry.Title>
					<Registry.TopBar
						alwaysShow
						className={cls.accountReportRegistryTopBar}
					>
						<Registry.Filters className={cls.accountReportRegistryFilters}>
							<DateField data-test-id='date-filter-date-input' />
							<TimeZoneField />
							<CurrenciesField />
						</Registry.Filters>
						<div className={cls.accountReportRegistryActions}>{topBarActionSlot}</div>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.Th>
									{t('account-report-registry.table-columns.account')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('account-report-registry.table-columns.currency')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('account-report-registry.table-columns.deposits')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('account-report-registry.table-columns.withdrawals')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('account-report-registry.table-columns.jackpots')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('account-report-registry.table-columns.bonuses')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('account-report-registry.table-columns.income')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('account-report-registry.table-columns.income-all-sources')}{' '}
									<Tooltip
										label={t('account-report-registry.table-columns.income-all-sources-tooltip')}
									>
										<HelpCircle />
									</Tooltip>
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('account-report-registry.table-columns.rtp')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('account-report-registry.table-columns.rtp-all-sources')}{' '}
									<Tooltip
										label={t('account-report-registry.table-columns.rtp-all-sources-tooltip')}
									>
										<HelpCircle />
									</Tooltip>
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('account-report-registry.table-columns.number-of-bets')}
								</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList>
								{(row) => (
									<>
										<Registry.Table.Td>
											<AccountLink
												data-test-id={`account-report-registry-account-${row.account.id}`}
												to={accountRoute}
												query={{ id: row.account.id }}
											>
												{row.account.name}
											</AccountLink>
										</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.deposits} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.withdrawals} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.jackpots} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.bonuses} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.income} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.incomeAllSources} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter
												value={row.rtp * 100}
												suffix=' %'
											/>
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter
												value={row.rtpAllSources * 100}
												suffix=' %'
											/>
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter
												value={row.numberOfBets}
												decimalScale={0}
											/>
										</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
						<AccountReportRegistryTotal />
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('account-report-registry.empty.title')}
						message={t('account-report-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						withClearFiltersButton={false}
						icon={<FileSearch />}
						title={t('account-report-registry.table-empty.title')}
						message={t('account-report-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return AccountReportRegistry;
};
